import * as React from 'react';

import type { PerceptionModelJobOption } from '@graphql/queries/PerceptionModelQueries';
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';

import RefreshLookupButton from './RefreshLookupButton';

/**
 * The top of the hierarchy: version → project → audience.
 *
 * Only versions that exported products are offered — a failed or in-flight run
 * has no peer lists, so picking one would look like a broken screen rather than
 * an unfinished job. The most recent run is the default.
 */

/**
 * Version ids carry a run uuid that nothing here needs, so only the date is
 * shown. Anything not matching the expected shape is left alone rather than
 * truncated blindly.
 *
 * ponytail: a date is not unique — two exported runs on the same day render the
 * same label. Add the job id to the option if that starts happening.
 */
export const shortVersion = (versionId: string): string =>
  /^v-\d{4}-\d{2}-\d{2}/.exec(versionId)?.[0] ?? versionId;

/**
 * pull_date is a calendar date, not an instant. Handing "2026-09-02" to
 * `new Date` gives UTC midnight, which renders as the day before anywhere west
 * of Greenwich, so the parts are read directly.
 */
export const formatDay = (value: string): string => {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

type Props = {
  jobs: PerceptionModelJobOption[];
  /** usable jobs that were filtered out, to explain a short list */
  hidden: number;
  value?: number;
  onChange: (jobId: number) => void;
};

const productCount = (j: PerceptionModelJobOption) =>
  j.perceptionModelProductLookupsByPerceptionModelJobId?.totalCount ?? 0;

const PerceptionModelPicker: React.FC<Props> = ({
  jobs,
  hidden,
  value,
  onChange,
}) => {
  const selected = jobs.find((j) => j.id === value);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          select
          fullWidth
          label="Perception model"
          value={value ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {jobs.map((j) => (
            <MenuItem key={j.id} value={j.id}>
              {j.universe} · {shortVersion(j.versionId)}
              {j.isDeployed && (
                <Chip
                  size="small"
                  label="deployed"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: 'text.secondary' }}
              >
                {productCount(j)} products
              </Typography>
            </MenuItem>
          ))}
        </TextField>

        <RefreshLookupButton jobId={value} />
      </Box>

      {selected && (
        <>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {productCount(selected)} products, based on reviews up to{' '}
            {formatDay(selected.pullDate)}.
          </Typography>
          <Typography
            variant="body2"
            sx={{ display: 'block', color: 'text.secondary' }}
          >
            Products reviewed since then are not in this model yet — use Refresh
            to add them.
          </Typography>
          {!selected.isDeployed && (
            <Typography variant="caption" sx={{ color: 'warning.main' }}>
              This is not the live model, so a norm set built here will not
              match production.
            </Typography>
          )}
        </>
      )}

      {hidden > 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary' }}
        >
          {hidden} other {hidden === 1 ? 'build is' : 'builds are'} still in
          progress or produced no products.
        </Typography>
      )}
    </Box>
  );
};

export default PerceptionModelPicker;
